import { useEffect, useMemo, useState } from "react";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import PageLayout from "components/PageLayout";
import OutlinedCard from "components/OutlinedCard";
import PageTitle from "components/PageTitle";
import TimeEntryTable from "./components/TimeEntryTable";
import TimeEntriesProvider from "./TimeEntriesContext";
import TimeEntryActions from "./components/TimeEntryActions";
import { useTimeEntrySearch } from "./useTimeEntrySearch";
import FilterMenu from "components/FilterMenu";
import TimeEntryFilters from "./components/TimeEntryFilters";
import SearchField from "components/SearchField";
import { SortColumn, useSortedData } from "lib/hooks/useSortedData";
import ViewSelectButton from "./components/ViewSelectButton";
import TimeEntrySchedule from "./components/TimeEntrySchedule";
import { ScheduleScale } from "features/schedule-view/ScheduleContext";
import { TimeEntry } from "models/TimeEntry";
import { useGetAllUsersLimitedQuery } from "app/api/users";
import { UserRole } from "features/auth/types/user";

const SORT_OPTIONS = [
  { key: "userName", defaultSortDir: "asc" },
  {
    key: "userHogiaNumber",
    comparator: (a, b, sortDir) => {
      const aNum = Number(a.userHogiaNumber) || 0;
      const bNum = Number(b.userHogiaNumber) || 0;
      return sortDir === "asc" ? aNum - bNum : bNum - aNum;
    },
  },
  { key: "createdAt" },
  { key: "type" },
  { key: "from" },
  { key: "to" },
  { key: "state" },
  { key: "processed" },
] as SortColumn<TimeEntry>[];

const TimeManagement: React.FC = () => {
  const [view, setView] = useQueryState<"LIST" | ScheduleScale>(
    "view",
    parseAsStringLiteral(["LIST", "WEEKS", "MONTHS", "WORKWEEKS"]).withDefault("LIST"),
  );

  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showDayNumbers, setShowDayNumbers] = useState(true);

  const {
    data,
    search,
    filters,
    activeFiltersCount,
    isLoading,
    setSearch,
    setFilters,
    resetFilters,
  } = useTimeEntrySearch({ view });

  const { data: employees } = useGetAllUsersLimitedQuery({
    roles: [
      UserRole.ADMIN,
      UserRole.USER_ADMIN,
      UserRole.PAYROLL,
      UserRole.INTERNAL_INSTALLER,
      UserRole.EMPLOYEE,
      UserRole.SALES,
    ],
    departmentIds: filters.departmentIds,
    officeIds: filters.officeIds,
  });

  const {
    data: sortedData,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
  } = useSortedData(data || [], SORT_OPTIONS);

  const sortedEmployees = useMemo(
    () =>
      employees
        ?.filter((emp) => showAllUsers || data?.some((entry) => entry.userId === emp.id))
        .sort((a, b) =>
          sortBy === "userName" && sortDir === "desc"
            ? b.userName.localeCompare(a.userName)
            : a.userName.localeCompare(b.userName),
        ) || [],
    [employees, sortBy, sortDir, data, showAllUsers],
  );

  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);

  // Clear selection whenever data changes
  useEffect(() => setSelectedEntries([]), [data]);

  return (
    <PageLayout maxWidth={false}>
      <PageTitle>Tidrapporter</PageTitle>

      <OutlinedCard>
        <TimeEntriesProvider
          data={sortedData}
          selectedEntries={selectedEntries}
          setSelectedEntries={setSelectedEntries}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <SearchField label="Sök namn" value={search} onChange={(e) => setSearch(e.target.value)} />

            <FilterMenu
              tooltip="Välj filter"
              activeFiltersCount={activeFiltersCount}
              onReset={resetFilters}
            >
              <Stack spacing={2}>
                <TimeEntryFilters filters={filters} onChange={setFilters} />
              </Stack>
            </FilterMenu>

            <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "flex-end" }}>
              <FormControlLabel
                label="Alla medarbetare"
                labelPlacement="start"
                hidden={view === "LIST"}
                control={
                  <Checkbox
                    checked={showAllUsers}
                    onChange={(e) => setShowAllUsers(e.target.checked)}
                  />
                }
              />

              <FormControlLabel
                label="Visa dagnummer"
                labelPlacement="start"
                hidden={view === "LIST"}
                sx={{ mr: 1 }}
                control={
                  <Checkbox
                    checked={showDayNumbers}
                    onChange={(e) => setShowDayNumbers(e.target.checked)}
                  />
                }
              />
            </Box>

            <ViewSelectButton view={view} setView={setView} />
          </Box>

          {isLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
              <CircularProgress />
              <Typography>Hämtar tidrapporter...</Typography>
            </Box>
          ) : view === "LIST" ? (
            <TimeEntryTable
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortDir={sortDir}
              setSortDir={setSortDir}
            />
          ) : (
            <TimeEntrySchedule
              rows={sortedEmployees}
              view={view}
              filters={filters}
              showDayNumbers={showDayNumbers}
              sortBy={sortBy}
              sortDir={sortDir}
              setSortBy={setSortBy}
              setSortDir={setSortDir}
            />
          )}

          <TimeEntryActions filters={filters} />
        </TimeEntriesProvider>
      </OutlinedCard>
    </PageLayout>
  );
};

export default TimeManagement;
